import React, { useEffect, useState, useContext } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTokens } from "../../styles/tokens";
import { usePanelStyles, ModalTitle } from "../FormFields/panel";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    body: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      alignItems: "center",
      gap: 28,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)", gap: 20 },
    },
    steps: { margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 14, counterReset: "passo" },
    step: {
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      fontFamily: t.font,
      fontSize: 14,
      lineHeight: 1.5,
      color: t.mode === "dark" ? t.text : t.label,
      counterIncrement: "passo",
      "&::before": {
        content: "counter(passo)",
        flex: "none",
        width: 24,
        height: 24,
        marginTop: -1,
        display: "grid",
        placeItems: "center",
        borderRadius: "50%",
        fontSize: 12,
        fontWeight: 600,
        color: t.brand,
        backgroundColor: t.tint.soft,
      },
      "& strong": { fontWeight: 600, color: t.text },
    },
    hint: { margin: "16px 0 0", fontSize: 12.5, lineHeight: 1.5, color: t.textSecondary },
    // o QR precisa de fundo branco para o celular ler, também no modo escuro
    qrTile: {
      justifySelf: "center",
      width: 288,
      maxWidth: "100%",
      minHeight: 288,
      padding: 16,
      boxSizing: "border-box",
      display: "grid",
      placeItems: "center",
      backgroundColor: "#ffffff",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      "& canvas, & svg": { display: "block" },
      [theme.breakpoints.down("xs")]: { order: -1 },
    },
    qrLoading: { backgroundColor: t.muted },
    loading: { display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center" },
    loadingText: { fontSize: 13, lineHeight: 1.5, color: t.textSecondary },
  };
});

const QrcodeModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const p = usePanelStyles();
  const [qrCode, setQrCode] = useState("");
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`/whatsapp/${whatsAppId}`);
        setQrCode(data.qrcode);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action === "update" && data.session.id === whatsAppId) {
        setQrCode(data.session.qrcode);
      }

      if (data.action === "update" && data.session.qrcode === "") {
        onClose();
      }
    };
    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, user.companyId]);

  const waitingText = i18n.exists("qrCode.waiting") ? i18n.t("qrCode.waiting") : "Gerando o QR Code…";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: p.dialogPaper }}
      aria-labelledby="qr-code-dialog"
    >
      <ModalTitle
        id="qr-code-dialog"
        title={i18n.t("qrCode.message")}
        subtitle="Conecte o WhatsApp do celular a este canal."
        onClose={onClose}
      />
      <DialogContent className={p.modalBody}>
        <div className={classes.body}>
          <div>
            <ol className={classes.steps}>
              <li className={classes.step}><span>Abra o <strong>WhatsApp</strong> no celular.</span></li>
              <li className={classes.step}><span>Toque em <strong>Mais opções</strong> (⋮) no Android ou em <strong>Configurações</strong> no iPhone.</span></li>
              <li className={classes.step}><span>Toque em <strong>Dispositivos conectados</strong> e depois em <strong>Conectar dispositivo</strong>.</span></li>
              <li className={classes.step}><span>Aponte a câmera do celular para este código.</span></li>
            </ol>
            <p className={classes.hint}>
              O código é renovado automaticamente. Esta janela fecha sozinha quando a conexão for concluída.
            </p>
          </div>
          {qrCode ? (
            <div className={classes.qrTile}>
              <QRCode
                value={qrCode}
                size={256}
                style={{ maxWidth: "100%", height: "auto" }}
              />
            </div>
          ) : (
            <div className={`${classes.qrTile} ${classes.qrLoading}`} role="status">
              <div className={classes.loading}>
                <CircularProgress size={28} />
                <span className={classes.loadingText}>{waitingText}</span>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
